import { CheckRounded } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React from "react";
import { components as reactSelectComponents, OptionProps } from "react-select";
import { FLOW_STEPS, FlowStep } from "./FlowStepCatalog";

/**
 * The "step to debug" dropdown. Options are grouped under a single heading so the list makes
 * clear these are the steps the KBo flow itself defines, and each row carries the step's skill
 * count on the right.
 */

type StepOption = {
    id: string;
    label: string;
    skillCount: number;
};

const GROUP_LABEL = "Steps defined in KBo flow";

function StepOptionRow(props: OptionProps<StepOption, false>) {
    const { data, isSelected } = props;
    return (
        <reactSelectComponents.Option {...props}>
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1,
                    width: "100%",
                }}
            >
                <Box component="span" sx={{ fontWeight: isSelected ? 700 : 500 }}>
                    {data.label}
                </Box>
                {isSelected && <CheckRounded fontSize="small" color="primary" />}
                <Box sx={{ flex: 1 }} />
                <Box
                    component="span"
                    sx={{ fontSize: 13, fontWeight: 600, opacity: 0.6 }}
                >
                    {data.skillCount} skill{data.skillCount === 1 ? "" : "s"}
                </Box>
            </Box>
        </reactSelectComponents.Option>
    );
}

export function FlowStepSelect({
    selectedStep,
    onChange,
}: {
    selectedStep: FlowStep;
    onChange: (step: FlowStep) => void;
}) {
    const theme = useTheme();

    const options: StepOption[] = React.useMemo(
        () =>
            FLOW_STEPS.map((step) => ({
                id: step.id,
                label: step.label,
                skillCount: step.skillNames.length,
            })),
        [],
    );

    return (
        <>
            <AcxSelectSingle
                id="flow-step-debug-step"
                options={[{ label: GROUP_LABEL, options }]}
                valueField="id"
                labelField="label"
                fullWidth
                maxMenuHeightPx={420}
                defaultValue={options.find((o) => o.id === selectedStep.id)}
                components={{ Option: StepOptionRow }}
                onChange={(option?: StepOption) => {
                    const step = FLOW_STEPS.find((s) => s.id === option?.id);
                    if (step) onChange(step);
                }}
            />
            <Typography
                variant="caption"
                sx={{ color: theme.palette.gray[500], display: "block", mt: 1.5 }}
            >
                The step you pick decides which fields the manual payload needs.
            </Typography>
        </>
    );
}
